import React, { useEffect, useState } from "react";
import { useRoleManagement } from "./hooks/useRoleManagement";
import { usePolicyManagement } from "./hooks/usePolicyManagement";
import { FEATURES, FeatureGate } from "../../lib/utils/featureAccess";
import DataTable from "../../lib/components/DataTable";
import { Edit, Trash2, SlidersHorizontal, Plus, Eye } from "lucide-react";
import { Button, Box, Snackbar, Alert, Chip } from "@mui/material";

const RoleManagement = () => {
  const { roles, loading, selectedRole, createRole, updateRole, deleteRole, selectRole, clearError } =
    useRoleManagement();
  const { policies: allPolicies } = usePolicyManagement();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewRole, setViewRole] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [formData, setFormData] = useState({
    roleId: "",
    name: "",
    description: "",
    policies: [],
    level: 0,
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (selectedRole) {
      setFormData({
        roleId: selectedRole.roleId || "",
        name: selectedRole.name || "",
        description: selectedRole.description || "",
        policies: selectedRole.policies || [],
        level: selectedRole.level || 0,
      });
      setShowCreateModal(true);
    }
  }, [selectedRole]);

  const columns = [
    { key: "roleId", label: "Role ID", sortable: true },
    { key: "name", label: "Name", sortable: true },
    { key: "description", label: "Description", sortable: true },
    {
      key: "policies",
      label: "Policies",
      sortable: false,
      render: value => (
        <div className="flex flex-wrap gap-1">
          {(value || []).map(policyId => (
            <Chip key={policyId} label={policyId} size="small" variant="outlined" sx={{ fontSize: "0.7rem" }} />
          ))}
        </div>
      ),
    },
    { key: "level", label: "Level", sortable: true },
    {
      key: "system",
      label: "System",
      sortable: true,
      render: value => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            value ? "bg-accent-light text-accent-dark" : "bg-gray-100 text-gray-600"
          }`}
        >
          {value ? "System" : "Custom"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      sortable: false,
      filterable: false,
      render: (value, row) => (
        <div className="flex gap-2">
          <FeatureGate featureId={FEATURES.USER_MANAGEMENT}>
            <button
              onClick={() => {
                setViewRole(row);
                setShowViewModal(true);
              }}
              className="text-gray-600 hover:text-blue-600"
              aria-label={`View role ${row.name}`}
            >
              <Eye size={18} />
            </button>
            <button
              onClick={() => selectRole(row)}
              className="text-gray-600 hover:text-accent"
              aria-label={`Edit role ${row.name}`}
            >
              <Edit size={18} />
            </button>
            <button
              onClick={async () => {
                if (window.confirm(`Are you sure you want to delete role "${row.name}"?`)) {
                  try {
                    await deleteRole(row.roleId);
                    setToastMessage("Role deleted successfully");
                    setShowToast(true);
                  } catch {
                    setToastMessage("Failed to delete role");
                    setShowToast(true);
                  }
                }
              }}
              className="text-red-600 hover:text-red-800"
              aria-label={`Delete role ${row.name}`}
            >
              <Trash2 size={18} />
            </button>
          </FeatureGate>
        </div>
      ),
    },
  ];

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      if (selectedRole) {
        await updateRole(selectedRole.roleId, formData);
        setToastMessage("Role updated successfully");
      } else {
        await createRole(formData);
        setToastMessage("Role created successfully");
      }
      setShowToast(true);
      setShowCreateModal(false);
      selectRole(null);
      setFormData({ roleId: "", name: "", description: "", policies: [], level: 0 });
      clearError();
    } catch {
      setToastMessage("Operation failed");
      setShowToast(true);
    }
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: name === "level" ? parseInt(value, 10) || 0 : value }));
  };

  const handlePolicyToggle = policyId => {
    setFormData(prev => ({
      ...prev,
      policies: prev.policies.includes(policyId)
        ? prev.policies.filter(p => p !== policyId)
        : [...prev.policies, policyId],
    }));
  };

  // Resolve policy IDs to their display names
  const getPolicyName = policyId => {
    const policy = allPolicies.find(p => p.policyId === policyId);
    return policy ? policy.name : policyId;
  };

  return (
    <FeatureGate featureId={FEATURES.USER_MANAGEMENT}>
      <div>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1.5, mb: 1.5 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setShowFilters(prev => !prev)}
            startIcon={<SlidersHorizontal size={15} />}
            sx={{
              textTransform: "none",
              borderColor: showFilters ? "var(--color-accent-active)" : "#d1d5db",
              borderRadius: "10px",
              color: showFilters ? "var(--color-accent-active)" : "#374151",
              backgroundColor: showFilters ? "var(--color-accent-alpha-06)" : "transparent",
              fontWeight: 600,
              fontSize: "0.8rem",
              px: 2,
              py: 0.7,
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: "var(--color-accent-active)",
                backgroundColor: "var(--color-accent-lightest)",
                color: "var(--color-accent-active)",
              },
            }}
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>
          <FeatureGate featureId={FEATURES.USER_MANAGEMENT}>
            <button
              onClick={() => {
                selectRole(null);
                setFormData({ roleId: "", name: "", description: "", policies: [], level: 0 });
                setShowCreateModal(true);
              }}
              className="flex items-center gap-2 btn-accent px-4 py-2 rounded"
            >
              <Plus size={16} />
              Add Role
            </button>
          </FeatureGate>
        </Box>

        <DataTable columns={columns} data={roles} loading={loading} showFilters={showFilters} pageSize={10} />

        {/* View Role Modal */}
        {showViewModal && viewRole && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[80vh] overflow-y-auto">
              <h2 className="text-xl font-bold mb-4">Role Details</h2>

              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-gray-500">Role ID</span>
                  <p className="text-sm text-gray-900">{viewRole.roleId}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Name</span>
                  <p className="text-sm text-gray-900">{viewRole.name || "\u2014"}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Description</span>
                  <p className="text-sm text-gray-900">{viewRole.description || "\u2014"}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Level</span>
                  <p className="text-sm text-gray-900">{viewRole.level}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Type</span>
                  <p className="text-sm text-gray-900">{viewRole.system ? "System" : "Custom"}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Attached Policies</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(viewRole.policies || []).length === 0 && <span className="text-sm text-gray-400">None</span>}
                    {(viewRole.policies || []).map(policyId => (
                      <Chip
                        key={policyId}
                        label={getPolicyName(policyId)}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4 mt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowViewModal(false);
                    setViewRole(null);
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit Role Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold mb-4">{selectedRole ? "Edit Role" : "Create Role"}</h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="roleId" className="block text-sm font-medium text-gray-700 mb-1">
                    Role ID *
                  </label>
                  <input
                    id="roleId"
                    type="text"
                    name="roleId"
                    value={formData.roleId}
                    onChange={handleInputChange}
                    disabled={!!selectedRole}
                    placeholder="e.g. TECH_SUPPORT"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-accent"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Display Name *
                  </label>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g. Technical Support"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-accent"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-accent"
                  />
                </div>

                <div>
                  <label htmlFor="level" className="block text-sm font-medium text-gray-700 mb-1">
                    Level
                  </label>
                  <input
                    id="level"
                    type="number"
                    name="level"
                    value={formData.level}
                    onChange={handleInputChange}
                    min={0}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-accent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Lower level = higher privilege (0 = superadmin)</p>
                </div>

                <div>
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className="block text-sm font-medium text-gray-700 mb-2">Attach Policies</label>
                  <div className="border border-gray-200 rounded-md max-h-48 overflow-y-auto p-2 space-y-1">
                    {allPolicies.length === 0 && (
                      <p className="text-sm text-gray-400 py-2 text-center">No policies available</p>
                    )}
                    {allPolicies.map(policy => (
                      <label
                        key={policy.policyId}
                        className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.policies.includes(policy.policyId)}
                          onChange={() => handlePolicyToggle(policy.policyId)}
                          className="w-4 h-4 text-accent border-gray-300 rounded focus:ring-accent"
                        />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium">{policy.name || policy.policyId}</span>
                          {policy.description && <p className="text-xs text-gray-500 truncate">{policy.description}</p>}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      selectRole(null);
                      setFormData({ roleId: "", name: "", description: "", policies: [], level: 0 });
                    }}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="px-4 py-2 btn-accent-submit rounded-md">
                    {selectedRole ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Toast */}
        <Snackbar
          open={showToast}
          autoHideDuration={3000}
          onClose={() => setShowToast(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setShowToast(false)}
            severity={toastMessage.includes("failed") || toastMessage.includes("Failed") ? "error" : "success"}
            sx={{ width: "100%" }}
          >
            {toastMessage}
          </Alert>
        </Snackbar>
      </div>
    </FeatureGate>
  );
};

export default RoleManagement;
